"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import Cookie from "js-cookie";
import { ModuleSettingsStore } from "../../../utils/contentstack/moduleSettingsStore.js";
import { DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION, validateDeliveryLocation } from "../../../utils/validations/deliveryLocations.js";
import { useState, useEffect } from "react";
import { Notification, SelectorRadioButtonGroup, Button } from "@nxt-costco-com/forge-components";
import { LEGACY_COOKIE_WC_BD_ZIP_ORIGINAL } from "../../Chrome/BrowseContext/constants.js";
import { formatPrice } from "../../Chrome/Header/ProgramTypeSurcharge/OrderStatusSurcharge/lib.js";
import { CostcoBox } from "../../Util/CostcoBox.js";
const deliveryMethodKeys = {
  localbusinessdeliveryoption: "Local",
  nationwidedeliveryoption: "Nationwide",
  pickupoption: "Store-Pick-Up"
};
function DeliveryMethodDrawer({
  agent,
  onClose,
  onSubmit,
  moduleSettings,
  deliveryMethodsConfig,
  zipCode,
  locale,
  warehouseSpecificDeliveryMethodUIOverrides
}) {
  const [selectedMethod, setSelectedMethod] = useState();
  const originalZipCode = Cookie.get(LEGACY_COOKIE_WC_BD_ZIP_ORIGINAL);
  const [backupDeliveryMethodConfig, setBackupDeliveryMethodConfig] = useState();
  const [error, setError] = useState(false);
  const [tmpErrorConfig, setTmpErrorConfig] = useState();
  const { composable_window, key_value, properties } = moduleSettings;
  const validationConfiguration = DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION[locale];
  const validationSchema = validationConfiguration == null ? void 0 : validationConfiguration.validationSchema;
  useEffect(() => {
    const getBackupDeliveryMethodsConfig = async () => {
      if (agent) {
        const { deliveryMethodsConfig: deliveryMethodsConfig2 } = await validateDeliveryLocation(
          agent,
          validationSchema,
          { zipCode: originalZipCode || zipCode },
          {
            isBDPage: true,
            onlyValidate: true,
            isInitiatedFromChangeAddressType: true
          },
          locale
        );
        setBackupDeliveryMethodConfig(deliveryMethodsConfig2);
      }
    };
    if (zipCode && !deliveryMethodsConfig && !backupDeliveryMethodConfig) {
      getBackupDeliveryMethodsConfig();
    }
  }, [
    agent,
    backupDeliveryMethodConfig,
    deliveryMethodsConfig,
    locale,
    setBackupDeliveryMethodConfig,
    validationSchema,
    zipCode,
    originalZipCode
  ]);
  if (!deliveryMethodsConfig && !backupDeliveryMethodConfig) {
    console.warn("DeliveryMethodDrawer - missing delivery method config");
    return null;
  }
  if (!agent) {
    return null;
  }
  if (!deliveryMethodsConfig && !zipCode) {
    console.error(
      "DeliveryMethodDrawer - missing delivery method config and zipcode"
    );
    return null;
  }
  const {
    isNationalDeliveryEnabledForWareHouse,
    isStorePickupEnabledForWareHouse,
    isBDTruckZoneEnabledForWareHouse
  } = deliveryMethodsConfig || backupDeliveryMethodConfig || {};
  const msStore = new ModuleSettingsStore({
    composable_window,
    key_value,
    properties
  });
  const primaryButtonText = msStore.getKeyValueItem("primarybuttonlabel");
  const secondaryButtonText = msStore.getKeyValueItem("secondarybuttonlabel");
  const optionKeys = msStore.getPropertiesItem(
    "optionKeys"
  );
  const icons = msStore.getPropertiesItem(
    "icons"
  );
  let filteredOptionKeys = optionKeys;
  if (!isStorePickupEnabledForWareHouse) {
    filteredOptionKeys = filteredOptionKeys.filter(
      (option) => option !== "pickupoption"
    );
  }
  if (!isNationalDeliveryEnabledForWareHouse) {
    filteredOptionKeys = filteredOptionKeys.filter(
      (option) => option !== "nationwidedeliveryoption"
    );
  }
  if (!isBDTruckZoneEnabledForWareHouse) {
    filteredOptionKeys = filteredOptionKeys.filter(
      (option) => option !== "localbusinessdeliveryoption"
    );
  }
  const options = filteredOptionKeys.map((option) => {
    const {
      option: optionOverride,
      body,
      icon,
      label,
      deliverySurcharge,
      freeDeliveryOrderAmount
    } = (warehouseSpecificDeliveryMethodUIOverrides == null ? void 0 : warehouseSpecificDeliveryMethodUIOverrides[option]) || {};
    const optionToUse = optionOverride || option;
    const deliverySurchargeToUse = deliverySurcharge || msStore.getKeyValueItem(`${optionToUse}deliverysurcharge`);
    const freeDeliveryOrderAmountToUse = freeDeliveryOrderAmount || msStore.getKeyValueItem(`${optionToUse}freedeliveryorderamount`);
    let bodyToUse = body || msStore.getComposableWindowItem(`${optionToUse}fillablebody`) || msStore.getComposableWindowItem(`${optionToUse}`) || "";
    if (deliverySurchargeToUse) {
      bodyToUse = bodyToUse.replace(
        "${deliverySurcharge}",
        formatPrice(locale, parseFloat(deliverySurchargeToUse), 0)
      );
    }
    if (freeDeliveryOrderAmountToUse) {
      bodyToUse = bodyToUse.replace(
        "${freeDeliveryOrderAmount}",
        formatPrice(
          locale,
          parseFloat(freeDeliveryOrderAmountToUse),
          0
        )
      );
    }
    return {
      isMarkdown: true,
      isTitleBold: true,
      body: bodyToUse,
      iconName: icon || icons[optionToUse] || null,
      title: label || msStore.getKeyValueItem(`${optionToUse}label`),
      value: option
    };
  });
  const handleSubmit = async ({
    userSelectionDeliveryMethod
  }) => {
    setError(false);
    if (userSelectionDeliveryMethod) {
      const response = await onSubmit(
        deliveryMethodKeys[userSelectionDeliveryMethod]
      );
      if (response == null ? void 0 : response.errorConfig) {
        setTmpErrorConfig(response.errorConfig);
      }
    } else {
      setError(true);
    }
  };
  return /* @__PURE__ */ jsxs(
    CostcoBox,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginTop: "16px"
      },
      children: [
        error && /* @__PURE__ */ jsx(
          Notification,
          {
            severity: "critical",
            message: "Select a delivery method."
          }
        ),
        tmpErrorConfig && /* @__PURE__ */ jsx(
          Notification,
          {
            severity: "alert",
            message: "Sorry, we can't update your delivery location right now. Please try again later."
          }
        ),
        /* @__PURE__ */ jsx(
          SelectorRadioButtonGroup,
          {
            onChange: function(value) {
              setSelectedMethod(value);
            },
            options,
            uniqueId: "radio-button-group"
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: () => {
              handleSubmit({
                userSelectionDeliveryMethod: selectedMethod
              });
            },
            children: primaryButtonText
          }
        ),
        /* @__PURE__ */ jsx(Button, { variant: "secondary", onClick: onClose, children: secondaryButtonText })
      ]
    }
  );
}
export {
  DeliveryMethodDrawer
};
